import React from 'react';
import { Leaf, Sparkles, Sun } from 'lucide-react';
import { getStyleCantripGrants } from '../../../shared/rules/fightingStyle.mjs';
import { MAGIC_INITIATE_LISTS, getMagicInitiateGrants } from '../../../shared/rules/magicInitiate.mjs';
import spellCatalog from '../../../data/spells.json';

const spellIdFromName = (name) => String(name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const catalogById = new Map(spellCatalog.map((spell) => [spellIdFromName(spell.name), spell]));
const STYLE_LOOK = Object.freeze({
  'druidic-warrior': { short: 'Druidic', accent: '#7cc576', Icon: Leaf },
  'blessed-warrior': { short: 'Blessed', accent: '#f2c94c', Icon: Sun },
  'magic-initiate': { short: 'Initiate', accent: '#b48cf2', Icon: Sparkles },
});

/** Style cantrips plus Magic Initiate (feat) cantrips, in the tile shape: { grantId, spellId, styleId, styleName, ability }. */
export const getCantripTileGrants = (form) => [...getStyleCantripGrants(form), ...getMagicInitiateGrants(form)
  .filter((grant) => grant.level === 0)
  .map((grant) => ({ ...grant, styleId: 'magic-initiate', styleName: `Magic Initiate (${MAGIC_INITIATE_LISTS[grant.list]})` }))];

/**
 * Cantrip Fighting Styles (Druidic Warrior for Ranger, Blessed Warrior for Paladin) and Magic Initiate: one
 * action-bar tile per chosen cantrip, like the Favored Enemy tile. Cantrips are at will; a tile is unavailable while its Action or
 * Bonus Action is spent.
 */
export default function ClassCantripControls({ form, characterId, combatState, isActiveTurn, blockReason, onCast }) {
  const grants = getCantripTileGrants(form);
  if (!grants.length) return null;
  const inCombat = Number.isInteger(combatState?.activeTurn) && Boolean(combatState?.participants?.[combatState.activeTurn]);
  const ledger = combatState?.turnActionLedger?.combatantId === String(characterId) && combatState.turnActionLedger.turnSequence === combatState.turnSequence ? combatState.turnActionLedger : null;
  const actionLeft = !ledger?.actionSpent || Boolean(ledger.actionSurge && !ledger.actionSurge.spent);
  const effects = Array.isArray(combatState?.activeEffects) ? combatState.activeEffects : [];
  return grants.map((grant) => {
    const { spellId, styleId, styleName } = grant;
    const spell = catalogById.get(spellId);
    const look = STYLE_LOOK[styleId] || STYLE_LOOK['druidic-warrior'];
    // Produce Flame: the Bonus Action conjures the flame; while it burns, the tile hurls it (Magic action).
    const hurl = spellId === 'produce-flame' && effects.some((effect) => effect?.definitionId === 'spell:produce-flame' && String(effect.targetCombatantId) === String(characterId));
    const name = hurl ? 'Hurl Flame' : spell?.name || spellId;
    const bonusAction = !hurl && spell?.actionType === 'bonusAction';
    const resourceLeft = bonusAction ? !ledger?.bonusActionSpent : actionLeft;
    const available = !blockReason && (!inCombat || (isActiveTurn && resourceLeft));
    const ability = grant.ability.charAt(0).toUpperCase() + grant.ability.slice(1);
    return (
      <button key={grant.grantId} type="button" data-action-tile={`cantrip:${grant.grantId}`} className="combat-hud-resource-tile combat-hud-feature-action-tile" style={{ '--hud-resource-accent': look.accent }}
        title={blockReason || `${name} · ${styleName} cantrip · ${bonusAction ? 'Bonus Action' : 'Action'} · ${ability}`}
        aria-label={`Cast ${name} (${styleName})`} disabled={!available} onClick={() => onCast?.(grant, { name, bonusAction, mode: hurl ? 'attack' : 'cast' })}>
        <span className="combat-hud-resource-tile__icon" aria-hidden="true"><look.Icon size={20} /></span>
        <span className="combat-hud-resource-tile__name">{name}</span>
        <span className="combat-hud-resource-tile__value">{look.short}</span>
      </button>
    );
  });
}
